import React, { useState, useRef, useEffect } from "react";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";
import emailjs from 'emailjs-com';

const HourlyBookingForm = ({ driverData }) => {
    function formatDateTime(dateTimeValue) {
        if (!dateTimeValue) return "—";
        const date = new Date(dateTimeValue);
        if (Number.isNaN(date.getTime())) return "—";
        const options = {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
        };
        return date.toLocaleDateString(undefined, options);
    }

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries: ["places"],
    });

    const BookingSuccess = () => {
        return (
            <div className="rounded-2xl bg-honest-surface-elevated border border-honest-gold/30 p-8 text-center">
                <div className="w-14 h-14 rounded-full bg-honest-gold/20 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-7 h-7 text-honest-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <h2 className="font-display text-xl font-semibold text-honest-cream mb-2">
                    Booking Submitted
                </h2>
                <p className="text-honest-cream-muted text-sm leading-relaxed">
                    We’ll reach out by text or call to confirm. Thank you for choosing Honest Limo.
                </p>
            </div>
        );
    };

    const [formData, setFormData] = useState({
        fullName: "",
        email: "",
        phone: "",
        pickupLocation: "",
        dateTime: "",
        specialInstructions: "",
        carType: "SUV",
        numPassengers: "1",
        paymentMethod: "pay-driver",
        totalCost: driverData.minRideCost || 70,
        numHours: 1,
    });

    const [showReview, setShowReview] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    const hourlyRate = driverData.minRideCost || 70;

    const pickupAutocomplete = useRef(null);

    const handlePlaceSelect = (place, field) => {
        setFormData((prevData) => ({
            ...prevData,
            [field]: place.formatted_address,
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    useEffect(() => {
        if (formData.numHours) {
            const cost = formData.numHours * hourlyRate;
            setFormData((prev) => ({ ...prev, totalCost: cost }));
        }
    }, [formData.numHours, hourlyRate]);

    const handleSubmit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        setSubmitError(null);
        setIsSubmitting(true);

        try {
            const emailBody = `
        You have received a new hourly booking request from a customer:

        Full Name: ${formData.fullName}
        Email: ${formData.email}
        Phone: ${formData.phone}
        Pickup Location: ${formData.pickupLocation}
        Date and Time: ${formData.dateTime}
        Special Instructions: ${formData.specialInstructions || "None"}
        Car Type: ${formData.carType}
        Number of Passengers: ${formData.numPassengers}
        Payment Method: ${formData.paymentMethod}
        Total Cost: $${Number(formData.totalCost).toFixed(2)}
        Number of Hours: ${formData.numHours}

        Best wishes,
        Honestlimo.com
      `;

            const emailParams = {
                to_email: driverData.email,
                message: emailBody,
                to_name: driverData.name,
                reply_to: formData.email,
                from_name: formData.fullName
            };

            const userId = process.env.REACT_APP_EMAILJS_USER_ID;
            if (!userId) {
                throw new Error("Email service is not configured.");
            }

            await emailjs.send(
                "default_service",
                "template_mmeyh3u",
                emailParams,
                userId
            );

            setFormData({
                fullName: "",
                email: "",
                phone: "",
                pickupLocation: "",
                dateTime: "",
                specialInstructions: "",
                carType: "SUV",
                numPassengers: "1",
                paymentMethod: "pay-driver",
                totalCost: hourlyRate,
                numHours: 1,
            });
            setShowReview(false);
            setIsSubmitted(true);
        } catch (error) {
            console.error("Error submitting booking:", error);
            setSubmitError(
                error?.text || error?.message || "Something went wrong. Please try again or call us to book."
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReviewClick = () => {
        setShowReview(true);
    };

    const inputClass =
        "mt-2 w-full px-4 py-3 rounded-xl bg-honest-surface border border-white/10 text-honest-cream placeholder-honest-cream-muted/70 focus:border-honest-gold focus:ring-1 focus:ring-honest-gold/30 transition-colors";
    const labelClass = "block text-sm font-medium text-honest-cream-muted mb-1";

    const SummaryComponent = () => {
        return (
            <div className="rounded-2xl bg-honest-surface-elevated border border-white/10 p-6 mb-6">
                <h2 className="font-display text-lg font-semibold text-honest-cream mb-4">Review Your Booking</h2>
                <dl className="space-y-2 text-sm">
                    <div className="flex justify-between"><dt className="text-honest-cream-muted">Total</dt><dd className="text-honest-gold font-semibold">${Number(formData.totalCost).toFixed(2)}</dd></div>
                    <div className="flex justify-between"><dt className="text-honest-cream-muted">Date & time</dt><dd className="text-honest-cream">{formatDateTime(formData.dateTime)}</dd></div>
                    <div className="flex justify-between"><dt className="text-honest-cream-muted">Hours</dt><dd className="text-honest-cream">{formData.numHours}</dd></div>
                    <div className="flex justify-between"><dt className="text-honest-cream-muted">Pickup</dt><dd className="text-honest-cream text-right max-w-[60%]">{formData.pickupLocation}</dd></div>
                    <div className="flex justify-between"><dt className="text-honest-cream-muted">Vehicle</dt><dd className="text-honest-cream">{formData.carType}</dd></div>
                    <div className="flex justify-between"><dt className="text-honest-cream-muted">Passengers</dt><dd className="text-honest-cream">{formData.numPassengers}</dd></div>
                    <div className="flex justify-between"><dt className="text-honest-cream-muted">Payment</dt><dd className="text-honest-cream">{formData.paymentMethod}</dd></div>
                    {formData.specialInstructions && (
                        <div><dt className="text-honest-cream-muted mb-1">Notes</dt><dd className="text-honest-cream">{formData.specialInstructions}</dd></div>
                    )}
                </dl>
                {submitError && (
                    <p className="mt-4 text-sm text-red-400" role="alert">{submitError}</p>
                )}
                <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap gap-3">
                    <button
                        type="button"
                        onClick={() => setShowReview(false)}
                        disabled={isSubmitting}
                        className="text-sm text-honest-gold hover:text-honest-gold-light transition-colors disabled:opacity-50"
                    >
                        Edit
                    </button>
                    <button
                        type="button"
                        onClick={(ev) => handleSubmit(ev)}
                        disabled={isSubmitting}
                        className="px-6 py-2.5 rounded-full bg-honest-gold text-honest-bg font-semibold hover:bg-honest-gold-light transition-colors disabled:opacity-50"
                    >
                        {isSubmitting ? "Sending…" : "Confirm & book"}
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div
            id="booking"
            className="rounded-2xl bg-honest-surface-elevated border border-white/10 p-6 sm:p-8"
        >
            {isSubmitted ? (
                <BookingSuccess />
            ) : showReview ? (
                <SummaryComponent />
            ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                        <label htmlFor="pickupLocation" className={labelClass}>Pickup location</label>
                        {isLoaded ? (
                            <Autocomplete
                                onLoad={(autocomplete) => {
                                    pickupAutocomplete.current = autocomplete;
                                    autocomplete.setFields(["formatted_address"]);
                                }}
                                onPlaceChanged={() =>
                                    handlePlaceSelect(
                                        pickupAutocomplete.current.getPlace(),
                                        "pickupLocation"
                                    )
                                }
                            >
                                <input
                                    type="text"
                                    id="pickupLocation"
                                    name="pickupLocation"
                                    value={formData.pickupLocation}
                                    onChange={handleInputChange}
                                    className={inputClass}
                                    placeholder="Address or place"
                                    required
                                />
                            </Autocomplete>
                        ) : (
                            <input
                                type="text"
                                id="pickupLocation"
                                name="pickupLocation"
                                value={formData.pickupLocation}
                                onChange={handleInputChange}
                                className={inputClass}
                                placeholder="Enter full address"
                                required
                            />
                        )}
                    </div>
                    <div>
                        <label htmlFor="dateTime" className={labelClass}>Date & time</label>
                        <input
                            type="datetime-local"
                            id="dateTime"
                            name="dateTime"
                            value={formData.dateTime}
                            onChange={handleInputChange}
                            min={new Date().toISOString().slice(0, 16)}
                            className={inputClass}
                            required
                        />
                    </div>
                    <div>
                        <label htmlFor="numHours" className={labelClass}>Number of hours</label>
                        <input
                            type="number"
                            id="numHours"
                            name="numHours"
                            value={formData.numHours}
                            onChange={handleInputChange}
                            min="1"
                            className={inputClass}
                            required
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="carType" className={labelClass}>Vehicle</label>
                            <select
                                id="carType"
                                name="carType"
                                value={formData.carType}
                                onChange={handleInputChange}
                                className={inputClass}
                            >
                                <option value="SUV">SUV</option>
                                <option value="Sedan">Sedan</option>
                            </select>
                        </div>
                        <div>
                            <label htmlFor="numPassengers" className={labelClass}>Passengers</label>
                            <input
                                type="number"
                                id="numPassengers"
                                name="numPassengers"
                                value={formData.numPassengers}
                                onChange={handleInputChange}
                                min="1"
                                className={inputClass}
                            />
                        </div>
                    </div>
                    <div>
                        <label htmlFor="paymentMethod" className={labelClass}>Payment</label>
                        <select
                            id="paymentMethod"
                            name="paymentMethod"
                            value={formData.paymentMethod}
                            onChange={handleInputChange}
                            className={inputClass}
                        >
                            <option value="pay-driver">Pay driver</option>
                            <option value="pay-now">Pay now</option>
                        </select>
                    </div>
                    <div>
                        <label htmlFor="specialInstructions" className={labelClass}>Special instructions (optional)</label>
                        <textarea
                            id="specialInstructions"
                            name="specialInstructions"
                            value={formData.specialInstructions}
                            onChange={handleInputChange}
                            rows="3"
                            className={inputClass}
                            placeholder="Access needs, luggage, etc."
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                            <label htmlFor="fullName" className={labelClass}>Full name</label>
                            <input
                                type="text"
                                id="fullName"
                                name="fullName"
                                value={formData.fullName}
                                onChange={handleInputChange}
                                className={inputClass}
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="email" className={labelClass}>Email</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                className={inputClass}
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="phone" className={labelClass}>Phone</label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                className={inputClass}
                                required
                            />
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-4 py-2 border-t border-white/10">
                        {submitError && (
                            <p className="w-full text-sm text-red-400" role="alert">{submitError}</p>
                        )}
                        <div className="text-sm">
                            <span className="text-honest-cream-muted">Hours </span>
                            <span className="text-honest-cream font-medium">{formData.numHours}</span>
                            <span className="text-honest-cream-muted ml-4">Total </span>
                            <span className="text-honest-gold font-semibold">${Number(formData.totalCost).toFixed(2)}</span>
                        </div>
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={handleReviewClick}
                                disabled={isSubmitting}
                                className="text-sm text-honest-gold hover:text-honest-gold-light transition-colors disabled:opacity-50"
                            >
                                Review
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="px-6 py-2.5 rounded-full bg-honest-gold text-honest-bg font-semibold hover:bg-honest-gold-light transition-colors disabled:opacity-50"
                            >
                                {isSubmitting ? "Sending…" : "Book ride"}
                            </button>
                        </div>
                    </div>
                </form>
            )}
        </div>
    );
};

export default HourlyBookingForm;
